import axios from 'axios';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { CloseIcon } from '@chakra-ui/icons';
import { Input, Button, Box, IconButton, Text, Heading, Flex } from "@chakra-ui/react";

function CreateTipPage() {
  const [title, setTitle] = useState<string>('');
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onDrop = (acceptedFiles: File[]) => {
    setImages([...images, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const validateInputs = (): boolean => {
    if (!title.trim()) {
      setError('Title is required.');
      return false;
    }
    if (!coverImage) {
      setError('Cover Image is required.');
      return false;
    }
    if (images.length === 0) {
      setError('At least one image is required.');
      return false;
    }
    setError('');
    return true;
  };

  const uploadImage = async (image: File): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append('file', image);
      formData.append('type', 'tips');
  
      let baseUrl = 'https://f8jcwt5745.execute-api.us-east-1.amazonaws.com/dev';
      if (localStorage.getItem("backend_env") === "prod") {
        baseUrl = 'https://vwt190bfa2.execute-api.us-east-1.amazonaws.com/prod';
      } else if (localStorage.getItem("backend_env") === "stag") {
        baseUrl = 'https://hp9zzz7tf8.execute-api.us-east-1.amazonaws.com/stag';
      }

      const accessToken = JSON.parse(localStorage.getItem("user")!).access_token;

      const response = await axios.post(
        `${baseUrl}/tips/upload-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
  
      return response.data.data.Location;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!validateInputs()) return;
  
    setIsLoading(true);
    try {
      // Upload cover image
      const coverImageUrl = await uploadImage(coverImage!);
  
      // Upload other images
      const imageUrls = [];
      for (const image of images) {
        const imageUrl = await uploadImage(image);
        imageUrls.push(imageUrl);
      }
  
      // Create tip with uploaded image URLs
      await createTip(title, coverImageUrl, imageUrls);
      alert('Tip created successfully!');
    } catch (error) {
      console.error(error);
      let errorMessage = 'Failed to create tip.';
  
      if (error instanceof Error) {
        // Handle known error types
        if (error.message) {
          errorMessage += ` Error message: ${error.message}`;
        }
  
        // Check if the error has a response property (e.g., axios errors)
        if ((error as any).response) {
          const response = (error as any).response;
          errorMessage += ` Server responded with status code ${response.status}: ${JSON.stringify(response.data)}`;
        } else if ((error as any).request) {
          // The request was made but no response was received
          errorMessage += ' No response received from server.';
        }
      } else {
        // Handle unknown error type
        errorMessage += ' An unknown error occurred.';
      }
  
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const createTip = (title: string, coverImageUrl: string, imageUrls: string[]) => {
    let baseUrl = 'https://f8jcwt5745.execute-api.us-east-1.amazonaws.com/dev';
    if (localStorage.getItem("backend_env") === "prod") {
      baseUrl = 'https://vwt190bfa2.execute-api.us-east-1.amazonaws.com/prod';
    } else if (localStorage.getItem("backend_env") === "stag") {
      baseUrl = 'https://hp9zzz7tf8.execute-api.us-east-1.amazonaws.com/stag';
    }

    const accessToken = JSON.parse(localStorage.getItem("user")!).access_token;

    return axios.post(
      `${baseUrl}/tips`,
      {
        title,
        coverImage: coverImageUrl,
        imageUrls: imageUrls,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', gap: 20 }}>
        <Input
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Flex direction="column" align="center">
          <Button onClick={() => document.getElementById('coverImageInput')?.click()}>
            {coverImage ? coverImage.name : 'Upload Cover Image'}
          </Button>
          <Input
            id="coverImageInput"
            type="file"
            display="none"
            onChange={(e) => setCoverImage(e.target.files ? e.target.files[0] : null)}
          />
        </Flex>
      </div>

      <Box mt={4}>
        <Heading size="md" mb={2}>Upload Images for Image URLs</Heading>
        <div {...getRootProps()} style={{ border: '2px dashed #ccc', padding: 20, textAlign: 'center' }}>
          <input {...getInputProps()} />
          <p>Drag & drop some images here, or click to select images</p>
        </div>
        <Box mt={2}>
          {images.map((file, index) => (
            <Box key={index} display="flex" alignItems="center" mt={2}>
              <Text>{file.name}</Text>
              <IconButton
                aria-label="Remove Image"
                icon={<CloseIcon />}
                onClick={() => setImages(images.filter((_, i) => i !== index))}
                ml={2}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {error && (
        <Text color="red" mt={4}>
          {error}
        </Text>
      )}

      <Button onClick={handleSubmit} mt={4} colorScheme="teal" isLoading={isLoading}>
        Publish Tip
      </Button>
    </div>
  );
};

export default CreateTipPage;
